import { useEffect, useState } from 'react'
import { Button, Message, Modal } from '@arco-design/web-react'
import { useSearchParams } from 'react-router-dom'
import { Spin } from '@arco-design/web-react'
import {
  IconBranch,
  IconDashboard,
  IconFire,
  IconGift,
  IconRobot,
  IconSettings,
  IconThunderbolt,
} from '@arco-design/web-react/icon'
import { useOptions } from '@/components/settings/useOptions'
import OperationSetting from '@/components/settings/OperationSetting'
import PaymentSetting from '@/components/settings/PaymentSetting'
import RatioSetting from '@/components/settings/RatioSetting'
import RateLimitSetting from '@/components/settings/RateLimitSetting'
import ModelSetting from '@/components/settings/ModelSetting'
import PerformanceSetting from '@/components/settings/PerformanceSetting'
import SystemSetting from '@/components/settings/SystemSetting'

const SETTING_TABS: Array<{
  key: string
  title: string
  icon: React.ReactNode
  Comp: React.ComponentType<{ options: ReturnType<typeof useOptions> }>
}> = [
  { key: 'operation', title: '运营', icon: <IconFire />, Comp: OperationSetting },
  { key: 'payment', title: '支付', icon: <IconGift />, Comp: PaymentSetting },
  { key: 'ratio', title: '分组/倍率', icon: <IconBranch />, Comp: RatioSetting },
  { key: 'ratelimit', title: '速率', icon: <IconThunderbolt />, Comp: RateLimitSetting },
  { key: 'models', title: '模型', icon: <IconRobot />, Comp: ModelSetting },
  { key: 'performance', title: '性能', icon: <IconDashboard />, Comp: PerformanceSetting },
  { key: 'system', title: '系统', icon: <IconSettings />, Comp: SystemSetting },
]

export default function SettingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromQuery = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(() => {
    if (tabFromQuery && SETTING_TABS.some((t) => t.key === tabFromQuery)) return tabFromQuery
    return 'operation'
  })
  const options = useOptions()
  // 访问过的 tab 保持挂载：切换左侧导航不再丢弃未保存的编辑
  const [visitedTabs, setVisitedTabs] = useState<Record<string, boolean>>({})
  useEffect(() => {
    setVisitedTabs((prev) => (prev[activeTab] ? prev : { ...prev, [activeTab]: true }))
  }, [activeTab])

  const { dirtyCount, discardChanges } = options
  useEffect(() => {
    if (!dirtyCount) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirtyCount])

  const confirmDiscard = () => {
    Modal.confirm({
      title: '放弃未保存的改动？',
      content: '这些改动只存在于当前页面，还没有写入服务端。',
      okText: '放弃改动',
      cancelText: '保留',
      onOk: () => {
        discardChanges()
        Message.success('已回到服务端当前配置')
      },
    })
  }

  useEffect(() => {
    const t = searchParams.get('tab')
    if (t && SETTING_TABS.some((x) => x.key === t)) {
      setActiveTab(t)
    }
  }, [searchParams])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', key)
      return next
    })
  }

  if (options.loading) {
    return (
      <div className="settings-page">
        <div className="settings-loading">
          <Spin />
        </div>
      </div>
    )
  }

  return (
    <div className="settings-page">
      <div className="settings-shell">
        <aside className="settings-sidebar" aria-label="系统设置导航">
          <div className="settings-sidebar-inner">
            {SETTING_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={
                  activeTab === tab.key
                    ? 'settings-nav-item settings-nav-item-active'
                    : 'settings-nav-item'
                }
                aria-current={activeTab === tab.key ? 'page' : undefined}
                onClick={() => handleTabChange(tab.key)}
              >
                <span className="settings-nav-text">{tab.title}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="settings-content">
          {dirtyCount > 0 ? (
            <div className="settings-info-strip mb-3 flex items-center justify-between gap-3">
              <span>
                有 <strong>{dirtyCount}</strong> 项改动还没保存（切换左侧分类不会丢失，但刷新会）。
              </span>
              <Button size="mini" onClick={confirmDiscard}>
                放弃改动
              </Button>
            </div>
          ) : null}
          {SETTING_TABS.map(({ key, Comp }) =>
            visitedTabs[key] ? (
              <div key={key} style={{ display: key === activeTab ? undefined : 'none' }}>
                <Comp options={options} />
              </div>
            ) : null
          )}
        </section>
      </div>
    </div>
  )
}
